import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Form, Input, InputNumber, Button, Divider, Spin, message } from "antd";
import { CheckCircleTwoTone, CloseCircleTwoTone, CopyOutlined, CheckOutlined } from "@ant-design/icons";
import axios, { getCurrentEnv } from "../../axiosConfig";

const HARDCODED = {
  behavior: "Contest",
  duration: 30,
  amount: 1,
  usage_limit: 1000,
  comment: "",
  expiration_date: "2026-03-30 00:00",
  leaderboard_name: "lions_roar_lb",
};

export default function CreateVendor() {
  const [form] = Form.useForm();
  const [flowStatus, setFlowStatus] = useState({ running: false, steps: [] });
  const [couponId, setCouponId] = useState(null);
  const [copied, setCopied] = useState(false);
  const existingVendors = useRef(new Set());

  useEffect(() => {
    axios.get("/vendors/all/").then((res) => {
      const vendors = res.data.vendors || [];
      existingVendors.current = new Set(vendors.map((v) => v.vendor_name));
    }).catch(() => {});
  }, []);

  function updateStep(index, status, errorMsg) {
    setFlowStatus((prev) => {
      const steps = prev.steps.map((s, i) =>
        i === index ? { ...s, status, error: errorMsg || undefined } : s
      );
      return { ...prev, steps };
    });
  }

  async function handleSubmit() {
    let data;
    try {
      data = await form.validateFields();
    } catch {
      return;
    }

    const steps = [
      { label: "Creating vendor", status: "pending" },
      { label: "Creating coupons", status: "pending" },
      { label: "Adding to leaderboard", status: "pending" },
    ];
    setFlowStatus({ running: true, steps });

    updateStep(0, "processing");
    try {
      await axios.post("/vendors/create/", {
        vendor_name: data.vendor_name,
        behavior: HARDCODED.behavior,
      });
    } catch (err) {
      updateStep(0, "error", err.response?.data?.error || "Failed to create vendor");
      return;
    }
    updateStep(0, "success");
    existingVendors.current.add(data.vendor_name);

    updateStep(1, "processing");
    try {
      const vendorsRes = await axios.get("/vendors/all/");
      const vendorsList = vendorsRes.data.vendors || [];
      const newVendor = vendorsList.find((v) => v.vendor_name === data.vendor_name);
      if (!newVendor) throw new Error("Vendor created but not found in vendor list");
      const couponRes = await axios.post("/coupons/create/", {
        vendor: newVendor.id,
        duration: HARDCODED.duration,
        amount: HARDCODED.amount,
        expiration_date: HARDCODED.expiration_date,
        usage_limit: HARDCODED.usage_limit,
        comment: HARDCODED.comment,
      });
      if (couponRes.data && couponRes.data.error) {
        throw new Error(couponRes.data.error);
      }
      try {
        const couponsRes = await axios.get(`/coupons/vendorid/?vendor_id=${newVendor.id}`);
        const coupons = couponsRes.data || [];
        if (coupons.length > 0) setCouponId(coupons[0].coupon_id);
      } catch {}

    } catch (err) {
      updateStep(1, "error", err.response?.data?.error || err.message || "Failed to create coupons");
      return;
    }
    updateStep(1, "success");

    updateStep(2, "processing");
    try {
      await axios.post("/dashboard/limit/leaderboard-data-update/", {
        l_name: HARDCODED.leaderboard_name,
        vendor_name: data.vendor_name,
        vendor_data: {
          display_name: data.display_name || "",
          city: data.city || "",
          childrean_num: data.childrean_num != null ? data.childrean_num : 0,
        },
      });
    } catch (err) {
      updateStep(2, "error", err.response?.data?.error || "Failed to add to leaderboard");
      return;
    }
    updateStep(2, "success");
  }

  function handleReset() {
    form.resetFields();
    setFlowStatus({ running: false, steps: [] });
    setCouponId(null);
    setCopied(false);
  }

  function copyLink() {
    if (!couponId) return;
    const env = getCurrentEnv();
    const base = env === "dev"
      ? "https://dev-dashboard.nick.academy"
      : "https://dashboard.nick.academy";
    const url = `${base}/vendors?coupon_id=${couponId}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      message.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    });
  }

  const finished = flowStatus.steps.length > 0 && !flowStatus.steps.some((s) => s.status === "processing" || s.status === "pending");

  return (
    <div style={{ width: "90%", maxWidth: 500, margin: "0 auto", padding: "24px 0" }}>
      <div style={{ marginBottom: 16 }}>
        <Link to="/vendors-list">← Vendors List</Link>
      </div>
      <h2 style={{ textAlign: "center", marginBottom: 24 }}>Create Vendor</h2>

      <Form
        form={form}
        layout="vertical"
        disabled={flowStatus.running}
        initialValues={{ childrean_num: 0 }}
      >
        <Divider orientation="left">Vendor</Divider>
        <Form.Item
          name="vendor_name"
          label="Vendor Name"
          normalize={(v) => (v || "").toLowerCase().replace(/[^a-z_]/g, "")}
          rules={[
            { required: true, message: "Vendor name is required" },
            { max: 10, message: "Max 10 characters" },
            { pattern: /^[a-z_]+$/, message: "Only lowercase letters and underscore" },
            {
              validator: (_, value) =>
                value && existingVendors.current.has(value)
                  ? Promise.reject("Vendor name already exists")
                  : Promise.resolve(),
            },
          ]}
        >
          <Input placeholder="vendor name (lowercase, a-z _)" maxLength={10} />
        </Form.Item>
        <Form.Item label="Behavior">
          <Input value={HARDCODED.behavior} disabled />
        </Form.Item>

        <Divider orientation="left">Coupons</Divider>
        <Form.Item label="Duration (days)">
          <InputNumber value={HARDCODED.duration} disabled style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item label="Number of coupons">
          <InputNumber value={HARDCODED.amount} disabled style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item label="Redeem limit">
          <InputNumber value={HARDCODED.usage_limit} disabled style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item label="Comment">
          <Input value={HARDCODED.comment} disabled placeholder="(none)" />
        </Form.Item>
        <Form.Item label="Expiration date">
          <Input value={HARDCODED.expiration_date} disabled />
        </Form.Item>

        <Divider orientation="left">Leaderboard</Divider>
        <Form.Item label="Leaderboard">
          <Input value={HARDCODED.leaderboard_name} disabled />
        </Form.Item>
        <Form.Item
          name="display_name"
          label="Display Name"
          rules={[{ required: true, message: "Display name is required" }]}
        >
          <Input placeholder="display name" />
        </Form.Item>
        <Form.Item name="city" label="City">
          <Input placeholder="city" />
        </Form.Item>
        <Form.Item name="childrean_num" label="Children Count">
          <InputNumber style={{ width: "100%" }} min={0} />
        </Form.Item>
      </Form>

      {flowStatus.steps.length > 0 && (
        <div style={{ marginTop: 16, marginBottom: 24 }}>
          <Divider orientation="left">Progress</Divider>
          {flowStatus.steps.map((step, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
              <span style={{ width: 24, marginRight: 12, display: "flex", justifyContent: "center" }}>
                {step.status === "processing" && <Spin size="small" />}
                {step.status === "success" && <CheckCircleTwoTone twoToneColor="#52c41a" />}
                {step.status === "error" && <CloseCircleTwoTone twoToneColor="#f5222d" />}
              </span>
              <span
                style={{
                  color: step.status === "pending" ? "#999" : step.status === "error" ? "#f5222d" : undefined,
                  fontWeight: step.status === "processing" ? "bold" : undefined,
                }}
              >
                {step.label}
                {step.error && <span style={{ marginLeft: 8, fontSize: 12 }}>— {step.error}</span>}
              </span>
            </div>
          ))}
        </div>
      )}

      <div style={{ textAlign: "center", marginTop: 16 }}>
        {!flowStatus.running && flowStatus.steps.length === 0 && (
          <Button type="primary" size="large" block onClick={handleSubmit}>
            Create
          </Button>
        )}
        {finished && couponId && (
          <Button
            size="large"
            block
            icon={copied ? <CheckOutlined /> : <CopyOutlined />}
            onClick={copyLink}
            style={{ marginBottom: 12 }}
          >
            {copied ? "Copied!" : "Copy Coupon Link"}
          </Button>
        )}
        {finished && (
          <Button type="primary" size="large" block onClick={handleReset}>
            Create Another
          </Button>
        )}
      </div>
    </div>
  );
}
